/**
 * Posting scenarios: what a person recorded, which one is active, and a way to try one on a phone.
 *
 * Recording is not done here — it needs a live phone, so it lives on the session panel. This page is
 * the catalogue: exactly one scenario is active (the service demotes the previous one), and that is what
 * every distribution replays. "Try on phone" runs a scenario now on one phone and follows the hub's
 * step-by-step log, so a scenario can be checked against the current TikTok build before it goes out
 * to a hundred phones.
 */

import { useState } from 'react'

import type { Device, Scenario } from '../../api/types'
import { phoneName } from '../devices/phoneName'
import { useT } from '../../i18n'
import { useApp } from '../../lib/context'
import { usePoll } from '../../lib/poll'
import { toast } from '../../lib/toast'
import { Badge, Card, Empty, LogView, Pill, Status } from '../../ui/components'
import { ConfirmDanger } from '../../ui/ConfirmDanger'
import { useJobTail } from '../distributions/useJobTail'

export function ScenariosPanel() {
  const { api, auth, authEpoch } = useApp()
  const t = useT()
  const list = usePoll(() => api.scenarios(), 5000, [api, authEpoch])
  const devices = usePoll(() => api.devices(), 10000, [api, authEpoch])
  const [toDelete, setToDelete] = useState<Scenario | null>(null)

  const act = async (fn: () => Promise<unknown>, done?: string) => {
    try {
      await fn()
      if (done) toast(done)
      list.refresh()
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), 'bad')
    }
  }

  const items = list.data?.scenarios ?? []
  return (
    <>
      <Card title={t.scenarios.title} sub={t.scenarios.sub}>
        <p className="dim small">{t.scenarios.rule}</p>
        <Status text={list.error ? t.scenarios.readError(list.error.message) : null} bad />
        {list.data && items.length === 0 && <Empty>{t.scenarios.none}</Empty>}
        {items.map((s) => (
          <ScenarioRow
            key={s.scenario_id}
            s={s}
            mayManage={auth.isAdmin}
            online={(devices.data ?? []).filter((d) => d.status !== 'offline')}
            onActivate={() => void act(() => api.activateScenario(s.scenario_id))}
            onDeactivate={() => void act(() => api.deactivateScenario(s.scenario_id))}
            onRename={(name) => void act(() => api.editScenario(s.scenario_id, { name }), t.scenarios.saved(name))}
            onCaption={(step) => void act(() => api.editScenario(s.scenario_id, { caption_step: step }), t.scenarios.captionMarked)}
            onDelete={() => setToDelete(s)}
          />
        ))}
      </Card>
      {toDelete && (
        <ConfirmDanger
          title={t.scenarios.deleteTitle(toDelete.name)}
          confirmLabel={t.scenarios.delete}
          holdSeconds={1}
          onCancel={() => setToDelete(null)}
          onConfirm={() => {
            const gone = toDelete
            setToDelete(null)
            void act(() => api.deleteScenario(gone.scenario_id), t.scenarios.deleted(gone.name))
          }}
        >
          <p className="dim small">{t.scenarios.deleteBody}</p>
        </ConfirmDanger>
      )}
    </>
  )
}

function ScenarioRow({
  s,
  mayManage,
  online,
  onActivate,
  onDeactivate,
  onRename,
  onCaption,
  onDelete,
}: {
  s: Scenario
  /** Changing the catalogue is admin-only on the service; the controls stay, disabled with the reason. */
  mayManage: boolean
  online: Array<Pick<Device, 'device_id' | 'name' | 'model' | 'serial' | 'hub_id'>>
  onActivate: () => void
  onDeactivate: () => void
  onRename: (name: string) => void
  onCaption: (step: number | null) => void
  onDelete: () => void
}) {
  const { api } = useApp()
  const t = useT()
  const [open, setOpen] = useState(false)
  const [renaming, setRenaming] = useState(false)
  const [name, setName] = useState(s.name)
  const [tryDevice, setTryDevice] = useState('')
  const [tryJob, setTryJob] = useState<string | null>(null)
  const tail = useJobTail(tryJob)
  const adminTitle = mayManage ? undefined : t.common.adminOnly
  const hasText = s.steps.some((x) => x.action === 'text')
  const captionAt = s.steps.findIndex((x) => x.action === 'text' && x.caption)

  const tryIt = async () => {
    if (!tryDevice) return
    try {
      const { job_id } = await api.tryScenario(s.scenario_id, tryDevice)
      setTryJob(job_id)
      toast(t.scenarios.tryStarted(tryDevice))
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), 'bad')
    }
  }

  return (
    <div style={{ borderTop: '1px solid var(--line-soft)', padding: 'var(--sp-3) 0' }}>
      <div className="row">
        {renaming ? (
          <input
            value={name}
            autoFocus
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && name.trim()) {
                onRename(name.trim())
                setRenaming(false)
              }
              if (e.key === 'Escape') setRenaming(false)
            }}
            onBlur={() => setRenaming(false)}
          />
        ) : (
          <b style={{ cursor: 'pointer' }} onClick={() => setOpen((v) => !v)}>
            {s.name}
          </b>
        )}
        {s.active && <Badge kind="ok">{t.scenarios.active}</Badge>}
        <span className="dim small">{t.scenarios.steps(s.steps.length)}</span>
        {s.recorded_on && <span className="dim tiny">{t.scenarios.recordedOn(s.recorded_on)}</span>}
        {hasText && <Badge kind={captionAt >= 0 ? 'ok' : 'warn'}>{captionAt >= 0 ? t.scenarios.captionStep : t.scenarios.hasText}</Badge>}
        <span className="grow" />
        {s.active ? (
          <button className="ghost btn-sm" disabled={!mayManage} title={adminTitle} onClick={onDeactivate}>
            {t.scenarios.deactivate}
          </button>
        ) : (
          <button className="primary btn-sm" disabled={!mayManage} title={adminTitle} onClick={onActivate}>
            {t.scenarios.activate}
          </button>
        )}
        <button className="ghost btn-sm" disabled={!mayManage} title={adminTitle} onClick={() => setRenaming(true)}>
          {t.scenarios.rename}
        </button>
        <button className="danger btn-sm" disabled={!mayManage} title={adminTitle} onClick={onDelete}>
          {t.scenarios.delete}
        </button>
      </div>
      {open && (
        <>
          <ol className="dim small" style={{ paddingLeft: '1.4em' }}>
            {s.steps.map((x, i) => (
              <li key={i}>
                {t.scenarios.stepKinds[x.action] ?? x.action}
                {x.target?.text || x.target?.content_desc ? ` · “${x.target.text || x.target.content_desc}”` : ''}
                {x.action === 'text' ? ` · “${String(x.text)}”` : ''}
                {x.action === 'key' ? ` · ${String(x.key)}` : ''}
                {x.pause_s ? ` · +${x.pause_s}s` : ''}
                {x.action === 'text' && (
                  <>
                    {' '}
                    {x.caption && <Badge kind="ok">{t.scenarios.captionStep}</Badge>}{' '}
                    <button className="chip" disabled={!mayManage} title={adminTitle} onClick={() => onCaption(x.caption ? null : i)}>
                      {x.caption ? t.scenarios.unmarkCaption : t.scenarios.markCaption}
                    </button>
                  </>
                )}
              </li>
            ))}
          </ol>
          {hasText && <p className="dim tiny">{t.scenarios.captionHint}</p>}
          <div className="row">
            <select value={tryDevice} onChange={(e) => setTryDevice(e.target.value)}>
              <option value="">{t.scenarios.tryPick}</option>
              {online.map((d) => (
                <option key={d.device_id} value={d.device_id}>
                  {phoneName(d)} · {d.hub_id ?? ''}
                </option>
              ))}
            </select>
            <button className="btn-sm" disabled={!tryDevice} onClick={() => void tryIt()}>
              {t.scenarios.tryOn}
            </button>
            {tryJob && <Pill mono>{tryJob.slice(0, 8)}</Pill>}
            {tail.job && <Badge kind={tail.job.status === 'done' ? 'ok' : tail.job.status === 'error' ? 'bad' : 'warn'}>{tail.job.status}</Badge>}
          </div>
          {tryJob && (
            <>
              <Status text={tail.error} bad />
              <LogView text={tail.log || t.distributions.emptyLog} height={160} />
            </>
          )}
        </>
      )}
    </div>
  )
}
